import React from 'react'
import styled from 'styled-components'
import {
  CurrencyInputItem,
  Page,
  PageContent,
  PageHeader,
  ApprovalButtons,
  SettingsButton,
  Plus,
  Receive,
  SuggestedDepositButton,
  DisabledMessage,
} from '../../common'
import { LiquidityState, useAddLiquidity } from './useAddLiquidity'
import { AddLiquidityButton } from './AddLiquidityButton'
import { PoolNavigation } from '../pool/PoolNavigation'
import { AddLiquidityInfo } from './AddLiquidityInfo'
import { Currency, CurrencyValue, formatCurrency } from '../../model'
import { ChainId, Colors, DISABLE_DEPOSIT, INTEGRAL_LP_SYMBOL } from '../../constants'
import { useEthers } from '../../hooks'
import { logAmplitudeEvent } from '../../utils'

export function AddLiquidity() {
  const {
    currencies,
    firstUsdValue,
    firstUsdValue18,
    firstValue,
    secondUsdValue,
    secondUsdValue18,
    secondValue,
    setFirstValue,
    setFirstMax,
    setSecondValue,
    setSecondMax,
    state,
    pair,
    liquidityValue,
    liquidityBalance,
    totalLiquiditySupply,
    suggestedDeposit,
  } = useAddLiquidity()

  function onSubmit() {
    setFirstValue('')
    setSecondValue('')
  }

  function onPairChange(currency: Currency, other: Currency) {
    setFirstValue('')
    setSecondValue('')
    currencies.setCurrencies(currency, other)
    logAmplitudeEvent('Deposit: Change Pair', {
      pair: `${currency.symbol}-${other.symbol}`,
    })
  }

  const balance = ((liquidityBalance && formatCurrency(18, liquidityBalance)) ?? '-').concat(' LP Tokens')

  const { chainId = ChainId.Mainnet } = useEthers()
  const disabled = pair && DISABLE_DEPOSIT.includes(pair.address.toLowerCase())
  if (chainId === ChainId.Mainnet && disabled) {
    return (
      <Page>
        <PageContent>
          <PageHeader label="Pool" currency={currencies.first} other={currencies.second} onChange={onPairChange} />
          <PoolNavigationStyled />
          <DisabledMessage>
            <p>Deposit on this pair is currently disabled.</p>
            <p>Please check back later or visit our Discord to learn more.</p>
          </DisabledMessage>
        </PageContent>
      </Page>
    )
  }

  return (
    <Page>
      <PageContent>
        <PageHeader label="Pool" currency={currencies.first} other={currencies.second} onChange={onPairChange} />
        <PoolNavigationStyled />
        <SuggestedDepositButton />
        <CurrencyInputItem
          label="You Provide"
          rate={firstUsdValue || '-'}
          value={firstValue}
          onValueChange={(value) => {
            setFirstValue(value)
            logAmplitudeEvent('Deposit: Input First', {
              pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
              value,
              symbol: currencies.first.symbol,
            })
          }}
          onMax={setFirstMax}
          currency={currencies.first}
        />
        <PlusContainer>
          <PlusStyled />
        </PlusContainer>
        <CurrencyInputItem
          label="You Provide"
          rate={secondUsdValue || '-'}
          value={secondValue}
          onValueChange={(value) => {
            setSecondValue(value)
            logAmplitudeEvent('Deposit: Input Second', {
              pair: `${currencies.first.symbol}-${currencies.second.symbol}`,
              value,
              symbol: currencies.second.symbol,
            })
          }}
          onMax={setSecondMax}
          currency={currencies.second}
        />
        <Hr />
        <ReceiveStyled
          pair={pair}
          symbol={INTEGRAL_LP_SYMBOL}
          value={liquidityValue ? formatCurrency(18, liquidityValue) : '-'}
          balance={balance}
        />
        <ApprovalButtons
          first={currencies.first}
          second={currencies.second}
          needFirstApproval={
            state === LiquidityState.NEED_BOTH_APPROVALS || state === LiquidityState.NEED_FIRST_APPROVAL
          }
          needSecondApproval={
            state === LiquidityState.NEED_BOTH_APPROVALS || state === LiquidityState.NEED_SECOND_APPROVAL
          }
        />
        {state !== LiquidityState.NEED_BOTH_APPROVALS && state !== LiquidityState.NEED_FIRST_APPROVAL && (
          <AddLiquidityButton
            currencies={currencies}
            state={state}
            firstValue={firstValue}
            secondValue={secondValue}
            firstUsdValue18={firstUsdValue18}
            secondUsdValue18={secondUsdValue18}
            onSubmit={onSubmit}
            pair={pair}
            suggestedDeposit={suggestedDeposit}
          />
        )}
        <AddLiquidityInfo
          pair={pair}
          firstValue={CurrencyValue.fromString(currencies.first, firstValue)}
          secondValue={CurrencyValue.fromString(currencies.second, secondValue)}
          liquidityBalance={liquidityBalance}
          totalLiquiditySupply={totalLiquiditySupply}
        />
        <SettingsButton />
      </PageContent>
    </Page>
  )
}

const PoolNavigationStyled = styled(PoolNavigation)`
  margin-bottom: 20px;
`

const PlusContainer = styled.div`
  text-align: center;
`

const PlusStyled = styled(Plus)`
  width: 10px;
`

const Hr = styled.hr`
  margin: 15px 20px;
  padding: 0;
  display: block;
  height: 1px;
  background-color: ${Colors.LightestGray};
  border: 0;
`

const ReceiveStyled = styled(Receive)`
  margin-top: 15px;
`
